import { useCallback, useState } from "react";
import { useNavigate } from "react-router";
import { type AxiosError, HttpStatusCode } from "axios";

import { QueryClientInstance } from "@vega-api/configs";
import {
  BFF_LOGOUT_PATH,
  clearSessionState,
  handleBffLogout,
  handleLogout,
  LOGIN_PATH,
  setLogoutInProgress,
} from "@vega-api/configs/interceptors/utils/logout";
import { AnalyticsEvent } from "@vega-api/custom/analytics/analyticsEvents";
import { useAnalytics } from "@vega-api/custom/hooks/useAnalytics";
import { useSession } from "@vega-api/custom/SessionContext/SessionContext";
import { setTxId } from "@vega-api/custom/stores/txId.store";
import { getErrorMessage } from "@vega-api/custom/utils/errorHandling";
import {
  useLogin,
  useLogout,
} from "@vega-api/generated/auth-login-v1/auth-login-v1";
import { type FrontendPortal } from "@vega-api/generated/types/frontendPortal";
import { type LoginUserRequest } from "@vega-api/generated/types/loginUserRequest";
import { OauthProvider } from "@vega-api/generated/types/oauthProvider";
import { getProfile } from "@vega-api/generated/user-management-v1/user-management-v1";
import { LocalStorageKeys } from "@vega-core/constants/cookies";
import { m } from "@vega-core/paraglide/messages";
import { LocalStorageService } from "@vega-core/service/LocalStorage.service";
import { isLocal } from "@vega-core/utils/global";
import { isFalsy } from "@vega-core/utils/isFalsy";
import { isTruthy } from "@vega-core/utils/isTruthy";
import { trackLoginFailed } from "@vega-core/utils/posthog/onboardingAnalytics";

export function useAuth(homePath = "/") {
  const session = useSession();
  const navigate = useNavigate();
  const { trackEvent } = useAnalytics();

  const [knownError, setKnownError] = useState<string | undefined>();
  const [unknownError, setUnknownError] = useState<string | undefined>();

  const { isPending: isLoginPending, mutate: signinMutation } = useLogin({
    mutation: {
      onMutate: () => setKnownError(undefined),
      onSuccess: async (response) => {
        if (
          isTruthy(response.data?.accessToken) &&
          isTruthy(response.data.refreshToken)
        ) {
          LocalStorageService.setToken(response.data.accessToken);
          LocalStorageService.setRefreshToken(response.data.refreshToken);
          LocalStorageService.setOauthProvider(OauthProvider.COGNITO);
        }

        if (isTruthy(response.data?.txId)) {
          setTxId(response.data.txId);
        }

        const { data } = await getProfile();

        if (data) {
          session.setUserData(data);
          trackEvent(AnalyticsEvent.AUTH_LOGIN_SUCCESS, {
            userId: data.id,
            userEligibilityType: data.extEligibility,
            userContactType: data.extContactType,
          });
        }

        const prevPath = window.localStorage.getItem(LocalStorageKeys.PrevPath);
        window.localStorage.removeItem(LocalStorageKeys.PrevPath);

        const path = prevPath ?? homePath;

        navigate(path);
      },
    },
  });

  const { isPending: isLogoutPending, mutateAsync: logoutMutation } = useLogout(
    {
      mutation: {
        onError: (error) => {
          console.error("Error while logging out:", error);

          const status = error.response?.status;

          if (
            status === HttpStatusCode.Unauthorized ||
            status === HttpStatusCode.Forbidden
          ) {
            session.clearUser();
            navigate(LOGIN_PATH);
          }
        },
        onSuccess: ({ redirectionUrl = "" }) => {
          const isBffPath = redirectionUrl.includes(BFF_LOGOUT_PATH);

          if (redirectionUrl && !isBffPath) {
            setLogoutInProgress(true);
            LocalStorageService.removeAllTokens();
            window.location.href = redirectionUrl;
          } else {
            void (isBffPath ? handleBffLogout() : handleLogout());
          }
        },
      },
    },
  );

  const login = useCallback(
    (loginValues: LoginUserRequest, onError?: (err: AxiosError) => void) => {
      signinMutation(
        { data: loginValues },
        {
          onError: (err: AxiosError) => {
            trackLoginFailed({ error: err });

            switch (err.status) {
              case HttpStatusCode.Unauthorized: {
                const { description } = getErrorMessage(err, {
                  defaultMessage: m.formValidation_invalidCredentials(),
                });
                setKnownError(description);
                break;
              }
              case HttpStatusCode.Forbidden: {
                const { description } = getErrorMessage(err, {
                  defaultMessage: m.formValidation_loginNotAllowed(),
                });
                setKnownError(description);
                break;
              }
              default: {
                const { description } = getErrorMessage(err, {
                  defaultMessage: m.errors_unexpectedError(),
                });
                setUnknownError(description);
              }
            }
            onError && onError(err);
          },
        },
      );
    },
    [signinMutation],
  );

  const logout = useCallback(
    async ({
      isGlobal = true,
      source,
    }: {
      isGlobal?: boolean;
      savePath: boolean;
      source: FrontendPortal;
    }) => {
      setLogoutInProgress(true);

      const refreshToken = LocalStorageService.getRefreshToken();
      const oauthProvider = LocalStorageService.getOauthProvider();
      const accessToken = LocalStorageService.getToken();

      if (isFalsy(refreshToken) && isFalsy(accessToken)) {
        session.clearUser();
        await handleBffLogout();

        return;
      }

      if (isGlobal && isTruthy(refreshToken)) {
        try {
          QueryClientInstance.cancelQueries();
          await logoutMutation({
            data: {
              refreshToken,
              oauthProvider: oauthProvider ?? OauthProvider.OKTA,
              accessToken,
              source,
              redirectToLocalhost: isLocal(),
            },
          });
        } catch {
          await handleBffLogout({ sessionExpired: true });

          return;
        }
      }

      if (oauthProvider === OauthProvider.COGNITO) {
        session.clearUser();
        clearSessionState();
      }
    },
    [logoutMutation, session],
  );

  return {
    isLoading: isLoginPending || isLogoutPending,
    login,
    loginError: knownError ?? unknownError,
    logout,
    unknownError,
  };
}
